import { useState } from "react"
import { MAX_URL_LENGTH } from "../../../../../constants/inputValidationDefaults"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { grey } from "@mui/material/colors"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Chip, MenuItem } from "@mui/material"
import {
    FormikDatePicker,
    FormikDropdown,
    FormikRichTextEditor,
    FormikTextField,
} from "../../../../../shared/components/forms/Formik"
import { CreateOrEditArticleFormValues, MerchantArticleStatus, MerchantArticleType } from "../merchantNewsItemType"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { ProductIdSearchField } from "./ProductIdSearchField"
import { BODY_LIMIT } from "./CreateOrEditArticleForm"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"

const useStyles = makeStyles()((theme) => ({
    chip: {
        color: grey[700],
        borderRadius: "0",
    },
}))
type CreateOrEditCreateOrEditArticleFormContentProps = Readonly<{
    isEditPage: boolean
}>

export function CreateOrEditArticleFormContent({
    isEditPage = false,
}: CreateOrEditCreateOrEditArticleFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateOrEditArticleFormValues>()
    const [bodyCharCount, setBodyCharCount] = useState(0)

    const { type, status, publishDate } = values

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    {isEditPage && (
                        <>
                            {status === MerchantArticleStatus.SCHEDULED && (
                                <LocalizedStrict
                                    id="create-or-edit-merchant-article-info-label-schedule"
                                    attrs={{ label: true }}
                                    vars={{ date: publishDate ? `${publishDate.toISODate()}` : "" }}
                                >
                                    <Chip
                                        icon={<FontAwesomeIcon icon={faCircleInfo} size="xl" />}
                                        label="Article is a schedule for publication on"
                                        className={classes.chip}
                                    />
                                </LocalizedStrict>
                            )}

                            {status === MerchantArticleStatus.DRAFT && (
                                <LocalizedStrict
                                    id="create-or-edit-merchant-article-info-label-draft"
                                    attrs={{ label: true }}
                                >
                                    <Chip
                                        icon={<FontAwesomeIcon icon={faCircleInfo} size="xl" />}
                                        label="Article is a draft. It will not be visible to users until you publish it."
                                        className={classes.chip}
                                    />
                                </LocalizedStrict>
                            )}
                        </>
                    )}
                    <LocalizedStrict id="create-or-edit-merchant-article-type" attrs={{ label: true }}>
                        <FormikDropdown<CreateOrEditArticleFormValues>
                            name="type"
                            label="Article typ"
                            fullWidth
                            defaultValue={type}
                            onChange={(e) => {
                                setFieldValue("type", e.target.value as MerchantArticleType)
                            }}
                        >
                            {Object.values(MerchantArticleType).map((item) => {
                                return (
                                    <MenuItem key={item} value={item}>
                                        <LocalizedEnum base="create-or-edit-merchant-article-mode" enumValue={item}>
                                            <> {item}</>
                                        </LocalizedEnum>
                                    </MenuItem>
                                )
                            })}
                        </FormikDropdown>
                    </LocalizedStrict>

                    <LocalizedStrict id="create-or-edit-merchant-article-title" attrs={{ label: true }}>
                        <FormikTextField<CreateOrEditArticleFormValues> name="subject" label="Title" fullWidth />
                    </LocalizedStrict>

                    {type === MerchantArticleType.EXTERNAL_ARTICLE && (
                        <FormikTextField<CreateOrEditArticleFormValues>
                            name="permaLink"
                            label="Link"
                            maxLength={MAX_URL_LENGTH}
                            fullWidth
                        />
                    )}
                    {type !== MerchantArticleType.EXTERNAL_ARTICLE && (
                        <LocalizedStrict
                            id="create-or-edit-merchant-article-body"
                            attrs={{ placeholder: true, helperText: true }}
                            vars={{ charsRemaining: BODY_LIMIT - bodyCharCount }}
                        >
                            <FormikRichTextEditor<CreateOrEditArticleFormValues>
                                name="body"
                                placeholder="Body"
                                helperText={`Characters remaining: ${BODY_LIMIT - bodyCharCount}`}
                                onCharacterCountChange={(charCount) => {
                                    setBodyCharCount(charCount)
                                }}
                            />
                        </LocalizedStrict>
                    )}

                    {type === MerchantArticleType.PRODUCT_NEWS && <ProductIdSearchField />}

                    {type !== MerchantArticleType.PRODUCT_NEWS && (
                        <LocalizedStrict id="news-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="leadImage" typeOfEntity="news" />
                        </LocalizedStrict>
                    )}
                </StandardMainPanel>
                <LocalizedStrict id="article-settings-panel-settings-title" attrs={{ title: true }}>
                    <StandardSidePanel title="Visibility">
                        <LocalizedStrict
                            id={
                                status === MerchantArticleStatus.PUBLISHED
                                    ? "article-settings-panel-published"
                                    : "article-settings-panel-schedule-for"
                            }
                            attrs={{ label: true }}
                        >
                            <FormikDatePicker
                                name="publishDate"
                                range="all"
                                label={status === MerchantArticleStatus.PUBLISHED ? "Published" : "Schedule"}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="article-settings-panel-expires" attrs={{ label: true }}>
                            <FormikDatePicker
                                name="expirationDate"
                                range="all"
                                label="Expires"
                            />
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </>
    )
}
